import { useEffect, useRef, useState } from 'react';
import { tagsApi } from './tagsApi';
import type { TagSuggestion } from '@/generated/api/models/tagSuggestion';

interface UseTagSuggestionsOptions {
  prefix: string;
  entityType?: string;
  enabled?: boolean;
  /** Debounce delay in ms. Default 150. */
  debounceMs?: number;
  limit?: number;
}

/**
 * Debounced ranked autocomplete for tags. Calls /api/v1/tags?prefix=... and
 * exposes the same ranked suggestions the design doc specifies (user
 * recency → in-module count → total count).
 */
export function useTagSuggestions({
  prefix,
  entityType,
  enabled = true,
  debounceMs = 150,
  limit = 20,
}: UseTagSuggestionsOptions): {
  suggestions: TagSuggestion[];
  loading: boolean;
  error: unknown;
} {
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const lastRequestId = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      return;
    }
    const requestId = ++lastRequestId.current;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await tagsApi.suggest({ prefix, entity_type: entityType, limit });
        if (requestId === lastRequestId.current) {
          setSuggestions(resp.suggestions);
        }
      } catch (err) {
        if (requestId === lastRequestId.current) {
          setError(err);
        }
      } finally {
        if (requestId === lastRequestId.current) {
          setLoading(false);
        }
      }
    }, debounceMs);
    return () => window.clearTimeout(handle);
  }, [prefix, entityType, enabled, debounceMs, limit]);

  return { suggestions, loading, error };
}
